import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

// Define toast types including warning
type ToastType = 'success' | 'error' | 'info' | 'warning';

// Interface for toast messages
interface ToastMessage {
  id: string; // Unique ID for each toast
  title?: string;
  message: string;
  description?: string;
  type?: ToastType;
  duration?: number;
  createdAt: number; // Timestamp for sorting
}

// Context interface
interface ToastContextType {
  showToast: (title: string, message: string, type?: ToastType, duration?: number) => void;
  showWebSocketToast: (title: string, description?: string) => void;
  dismissToast: (id: string) => void;
  toasts: ToastMessage[];
}

// Maximum number of toasts to show at once
const MAX_TOASTS = 5;

// Create context without exporting it directly
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Hook to use the toast context without exporting it directly
const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Generate a unique ID for each toast
const generateId = () => Math.random().toString(36).substring(2, 9);

// Toast provider component
const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  // Function to add a new toast
  const addToast = useCallback((toast: Omit<ToastMessage, 'id' | 'createdAt'>) => {
    const id = generateId();
    const newToast = { 
      ...toast, 
      id, 
      createdAt: Date.now() 
    };

    setToasts(prev => {
      // Limit the number of toasts
      const updatedToasts = [newToast, ...prev].slice(0, MAX_TOASTS);
      return updatedToasts;
    });

    // Auto-dismiss after duration
    if (toast.duration) {
      setTimeout(() => {
        dismissToast(id);
      }, toast.duration);
    }
  }, []);

  // Function to show a toast
  const showToast = useCallback((
    title: string, 
    message: string, 
    type: ToastType = 'success',
    duration: number = 5000
  ) => {
    addToast({ title, message, type, duration });
  }, [addToast]);

  // Special method for WebSocket notifications
  const showWebSocketToast = useCallback((title: string, description?: string) => {
    addToast({ 
      title, 
      message: 'Nova notificação chegou', 
      description: description || 'Você recebeu uma nova notificação',
      type: 'info',
      duration: 6000
    });
  }, [addToast]);

  // Function to dismiss a toast
  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Clean up old toasts
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setToasts(prev => 
        prev.filter(toast => !toast.duration || now - toast.createdAt < toast.duration)
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Get icon based on toast type
  const getToastIcon = (type: ToastType = 'info') => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
          </svg>
        );
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, showWebSocketToast, dismissToast, toasts }}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <div 
            key={toast.id}
            className={`flex items-start p-4 rounded-lg shadow-lg transform transition-all duration-300 ease-in-out 
              ${toast.type === 'success' ? 'bg-green-500' : 
                toast.type === 'error' ? 'bg-red-500' : 
                toast.type === 'warning' ? 'bg-yellow-500' :
                'bg-blue-500'
              } text-white max-w-md animate-fade-in`}
            style={{
              animation: 'fadeIn 0.3s ease-in-out'
            }}
          >
            <div className="flex-shrink-0 mr-3">
              {getToastIcon(toast.type)}
            </div>
            <div className="flex-1">
              {toast.title && <p className="font-bold">{toast.title}</p>}
              <p>{toast.message}</p>
              {toast.description && <p className="text-sm mt-1 opacity-90">{toast.description}</p>}
            </div>
            <button 
              onClick={() => dismissToast(toast.id)}
              className="ml-3 text-white opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </button>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }
      `}</style>
    </ToastContext.Provider>
  );
};

// Export everything together
const ToastContextExports = {
  Provider: ToastProvider,
  Context: ToastContext,
  useToast
};

export default ToastContextExports;
